/*
 * Copyright (c) 2024. Tooling Team
 */

import { createApi } from "@reduxjs/toolkit/query/react";
import { MEDDOMAIN_BASE_URL } from "../../utils/constants";
import HeaderService from "../../services/headerService";
import {
  setMedDomainBuildRowData,
  setMedDomainBuildSummaryData,
  setMedDomainBuildSummaryEisData,
  setMedDomainBuildDetailData,
} from "../slices/medDomain.slice";
import { addUniqueId } from "../../utils/funcs";
import { normalizeMedDomainDetailRecord } from "./medDomainTransforms";
import { createAuthenticatedBaseQuery } from "./authenticatedBaseQuery";

const MED_DOMAIN_QUERY_LIMIT = 100000;

// This API bypasses baseApi error handling and calls the AWS ECS service
// Use this for API calls that need custom error handling or point to the ECS service

// Extract endpoint definitions to a shared function for reuse
export const medDomainEndpoints = (builder) => ({
  getAllMedDomainBuilds: builder.query({
    query: () => `/v1/builds/list`,
    transformResponse: (response) => response.builds ?? response.records ?? [],
    async onQueryStarted(arg, { dispatch, queryFulfilled }) {
      try {
        const builds = await queryFulfilled;
        // Add uniqueId to each build object- will be removed once we have a BE UUID
        const buildsWithUniqueId = addUniqueId(builds.data || []);
        dispatch(setMedDomainBuildRowData(buildsWithUniqueId));
      } catch (e) {
        console.log("Error fetching all MedDomain builds: ", e);
      }
    },
  }),
  getMedDomainBuildSummaryById: builder.query({
    query: (buildId) => `/v1/builds/summary/${buildId}`,
    transformResponse: (response) => response.summaries ?? [],
    async onQueryStarted(arg, { dispatch, queryFulfilled }) {
      try {
        const summary = await queryFulfilled;
        const summaryWithUniqueId = addUniqueId(summary.data || []);
        dispatch(setMedDomainBuildSummaryData(summaryWithUniqueId));
      } catch (e) {
        console.log("Error fetching build summary: ", e);
      }
    },
  }),
  getMedDomainBuildSummaryEisById: builder.query({
    query: (buildId) => `/build/summary/eis/${buildId}`,
    transformResponse: (response) =>
      response.summaries ?? response.records ?? [],
    async onQueryStarted(arg, { dispatch, queryFulfilled }) {
      try {
        const summary = await queryFulfilled;
        const summaryWithUniqueId = addUniqueId(summary.data || []);
        dispatch(setMedDomainBuildSummaryEisData(summaryWithUniqueId));
      } catch (e) {
        console.log("Error fetching EIS build summary: ", e);
      }
    },
  }),
  getMedDomainBuildDetail: builder.mutation({
    query: (arg) => {
      const buildId = typeof arg === "object" ? arg.buildId : arg;
      const mapUsed =
        typeof arg === "object" && arg.mapUsed ? arg.mapUsed : "null";

      return {
        url: `v1/builds/detail`,
        method: "POST",
        body: {
          build_id: parseInt(buildId),
          map_used: mapUsed,
          offset: 0,
          limit: MED_DOMAIN_QUERY_LIMIT,
        },
      };
    },
    transformResponse: (response) =>
      (response.results ?? []).map((record) =>
        normalizeMedDomainDetailRecord(record)
      ),
    transformErrorResponse: (response) =>
      response.results ?? { error: `An error occurred ${response.status}` },
    async onQueryStarted(arg, { dispatch, queryFulfilled }) {
      try {
        const detail = await queryFulfilled;
        const detailWithUniqueId = addUniqueId(detail.data || []);
        dispatch(setMedDomainBuildDetailData(detailWithUniqueId));
      } catch (e) {
        console.log("Error fetching build detail: ", e);
      }
    },
  }),
  getMedDomainBuildDiff: builder.mutation({
    query: (arg) => {
      const { groupId, newBuildId, oldBuildId } = arg;
      return {
        url: `v1/builds/diff`,
        method: "POST",
        body: {
          group_id: groupId,
          new_build_id: parseInt(newBuildId),
          old_build_id: parseInt(oldBuildId),
          offset: 0,
          limit: MED_DOMAIN_QUERY_LIMIT,
        },
      };
    },
    transformResponse: (response) => {
      if (response && typeof response === "object") {
        if (Array.isArray(response.diffResults)) {
          return {
            ...response,
            diffResults: response.diffResults,
          };
        }
        return response;
      }
      return { diffResults: [] };
    },
    transformErrorResponse: (response) =>
      response.diffResults ?? {
        error: `An error occurred ${response.status}`,
      },
  }),
  createMedDomainDiff: builder.mutation({
    query: ({ groupId, newBuildId, oldBuildId }) => ({
      url: `v1/diff`,
      method: "POST",
      body: {
        group_id: groupId,
        new_build_id: parseInt(newBuildId),
        old_build_id: parseInt(oldBuildId),
      },
    }),
    transformResponse: (response) => {
      if (response && typeof response.diff_id === "number") {
        return response.diff_id;
      }
      if (typeof response === "number") return response;
      if (response && typeof response.buildId === "number") {
        return response.buildId;
      }
      return response;
    },
    transformErrorResponse: (response) => {
      if (response && typeof response === "object") {
        return response.data ?? response;
      }
      return response;
    },
  }),
  getMedDomainDiffStatus: builder.query({
    query: (diffBuildId) => `/v1/diff/status/${diffBuildId}`,
    transformResponse: (response) => response,
  }),
});

const headerService = new HeaderService();

const medDomainBaseQuery = createAuthenticatedBaseQuery({
  baseUrl: MEDDOMAIN_BASE_URL,
  getHeaders: () => headerService.getAuthorizationHeader(),
});

export const medDomainApi = createApi({
  reducerPath: "medDomainApi",
  baseQuery: medDomainBaseQuery,
  endpoints: medDomainEndpoints,
});

export const {
  useGetAllMedDomainBuildsQuery,
  useLazyGetAllMedDomainBuildsQuery,
  useGetMedDomainBuildSummaryByIdQuery,
  useLazyGetMedDomainBuildSummaryByIdQuery,
  useGetMedDomainBuildSummaryEisByIdQuery,
  useLazyGetMedDomainBuildSummaryEisByIdQuery,
  useGetMedDomainBuildDetailMutation,
  useGetMedDomainBuildDiffMutation,
  useCreateMedDomainDiffMutation,
  useGetMedDomainDiffStatusQuery,
} = medDomainApi;

export const selectGetAllMedDomainBuilds =
  medDomainApi.endpoints.getAllMedDomainBuilds.select();
